import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Button, Badge, Alert, Modal, Table, Spinner, Form } from 'react-bootstrap';
import store from '../../store';
import TimeSeriesChart from './TimeSeriesChart';
import MessageDetails from './MessageDetails';
import monitoringDataProcessor from '../../services/MonitoringDataProcessor';
import { getSources } from '../../store/sourcesSlice';
import { formatUTCTime } from '../../utils/timeUtils';
import { useWebSocketContext } from '../../contexts/WebSocketContext';
import {
  clearMonitoringMessages,
  clearMonitoringData,
  setPersistentSourceColors,
  setPersistentSourceOrder,
  setSourceLastSeen,
  setSelectedInterval,
  setSelectedIntervalMessages,
  setSelectedSource,
} from '../../store/monitoringSlice';
import LatencyStatsDisplay from './LatencyStatsDisplay';

// Cache for source name lookups to prevent repeated API calls and calculations
const sourceNameCache = new Map();

// Predefined color palette for sources
const DISTINCT_COLORS = [
  '#4e79a7', '#f28e2c', '#e15759', '#76b7b2', '#59a14f',
  '#edc949', '#af7aa1', '#ff9da7', '#9c755f', '#bab0ab'
];

const TIME_WINDOW_OPTIONS = [
  { value: 30 * 1000, label: '30 sec' },
  { value: 1 * 60 * 1000, label: '1 min' },
  { value: 5 * 60 * 1000, label: '5 min' },
  { value: 10 * 60 * 1000, label: '10 min' },
  { value: 30 * 60 * 1000, label: '30 min' },
  { value: 60 * 60 * 1000, label: '1 hr' }
];

const RATE_INTERVAL_OPTIONS_BY_WINDOW = {
  [30 * 1000]: [
    { value: 1 * 1000, label: '1 sec' },
    { value: 5 * 1000, label: '5 sec' },
    { value: 10 * 1000, label: '10 sec' }
  ],
  [1 * 60 * 1000]: [
    { value: 1 * 1000, label: '1 sec' },
    { value: 5 * 1000, label: '5 sec' },
    { value: 10 * 1000, label: '10 sec' },
    { value: 30 * 1000, label: '30 sec' }
  ],
  [5 * 60 * 1000]: [
    { value: 1 * 1000, label: '1 sec' },
    { value: 5 * 1000, label: '5 sec' },
    { value: 10 * 1000, label: '10 sec' },
    { value: 30 * 1000, label: '30 sec' }
  ],
  [10 * 60 * 1000]: [
    { value: 1 * 1000, label: '1 sec' },
    { value: 5 * 1000, label: '5 sec' },
    { value: 10 * 1000, label: '10 sec' },
    { value: 30 * 1000, label: '30 sec' },
    { value: 1 * 60 * 1000, label: '1 min' }
  ],
  [30 * 60 * 1000]: [
    { value: 10 * 1000, label: '10 sec' },
    { value: 30 * 1000, label: '30 sec' },
    { value: 1 * 60 * 1000, label: '1 min' },
    { value: 5 * 60 * 1000, label: '5 min' }
  ],
  [60 * 60 * 1000]: [
    { value: 30 * 1000, label: '30 sec' },
    { value: 1 * 60 * 1000, label: '1 min' },
    { value: 5 * 60 * 1000, label: '5 min' },
    { value: 10 * 60 * 1000, label: '10 min' }
  ]
};

// Generate a deterministic color based on source ID
const getSourceColor = (sourceId, existingColors = {}) => {
  if (!sourceId) return '#8884d8'; // Default color if no source ID

  // If we already have a color for this source, return it
  if (existingColors[sourceId]) {
    return existingColors[sourceId];
  }

  // Generate a deterministic hash for this source ID
  let hash = 0;
  for (let i = 0; i < sourceId.length; i++) {
    hash = sourceId.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Use the hash to select a color from our distinct palette first
  // This ensures the same source always gets the same color from the palette
  const paletteIndex = Math.abs(hash) % DISTINCT_COLORS.length;
  const paletteColor = DISTINCT_COLORS[paletteIndex];

  // Check if this palette color is already used by another source
  const usedColors = new Set(Object.values(existingColors));
  if (!usedColors.has(paletteColor)) {
    return paletteColor;
  }

  // If the preferred palette color is taken, generate a unique HSL color
  // Use golden ratio to help distribute colors more evenly
  const goldenRatioConjugate = 0.618033988749895;
  let hue = (Math.abs(hash) * goldenRatioConjugate) % 1;

  // Scale to get a value between 0-360 for HSL
  hue = Math.floor(hue * 360);

  // Use high saturation and medium lightness for vibrant, distinguishable colors
  const saturation = 70;
  const lightness = 50;

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Monitoring Dashboard component
 * Displays real-time data from WebSocket connection
 * WebSocket connection is managed globally in App.js
 * WebSocket context is provided at the route level
 */
const MonitoringDashboard = () => {
  const dispatch = useDispatch();
  const { sources } = useSelector((state) => state.sources);

  // Get global WebSocket functions
  const { connect: connectWebSocket, disconnect: disconnectWebSocket } = useWebSocketContext();

  // Get WebSocket state from Redux (managed globally)
  const {
    connected,
    connecting,
    error,
    userDisconnected,
  } = useSelector((state) => state.websocket);

  // Get monitoring-specific state from monitoring slice
  const {
    timeSeriesData,
    selectedInterval,
    selectedIntervalMessages,
    lastUpdateTime,
    persistentSourceColors = {},
    persistentSourceOrder = [],
    sourceLastSeen = {},
    selectedSource = 'total',
    totalMessagesReceived = 0,
    latencyStats = { p50: null, p95: null, p99: null },
    messageRate = null,
  } = useSelector((state) => state.monitoring);

  // Local state for UI
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedTimeWindowMs, setSelectedTimeWindowMs] = useState(5 * 60 * 1000);
  const [rateIntervalMs, setRateIntervalMs] = useState(10 * 1000);
  const [messagesPerSecond, setMessagesPerSecond] = useState(null);


  // Interval settings
  const sourceActivityTimeout = 60 * 60 * 1000; // 1 hour
  const rateIntervalOptions = useMemo(() => {
    return RATE_INTERVAL_OPTIONS_BY_WINDOW[selectedTimeWindowMs] || RATE_INTERVAL_OPTIONS_BY_WINDOW[5 * 60 * 1000];
  }, [selectedTimeWindowMs]);

  useEffect(() => {
    if (!rateIntervalOptions.some(option => option.value === rateIntervalMs)) {
      setRateIntervalMs(rateIntervalOptions[0].value);
    }
  }, [rateIntervalMs, rateIntervalOptions]);

  // Fetch sources on component mount and clear cache when sources change
  useEffect(() => {
    dispatch(getSources());
    // Note: MonitoringDataProcessor is now managed globally in App.js
    // and runs continuously while the user is authenticated
  }, [dispatch]);

  // Clear source name cache when sources change to ensure fresh lookups
  useEffect(() => {
    if (sources && sources.length > 0) {
      sourceNameCache.clear();
      if (process.env.NODE_ENV === 'development') {
        console.log('MonitoringDashboard: Cleared source name cache due to sources update');
      }
    }
  }, [sources]);

  // Periodic cleanup of inactive sources
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      // Get current state values inside the interval to avoid stale closures
      const state = store.getState();
      const currentPersistentSourceOrder = state.monitoring?.persistentSourceOrder || [];
      const currentSourceLastSeen = state.monitoring?.sourceLastSeen || {};
      const currentPersistentSourceColors = state.monitoring?.persistentSourceColors || {};
      const currentTime = Date.now();

      // Filter out sources that haven't been seen within the active source window.
      const activeSources = currentPersistentSourceOrder.filter(sourceId => {
        const lastSeen = currentSourceLastSeen[sourceId];
        return lastSeen && (currentTime - lastSeen) < sourceActivityTimeout;
      });

      // Update order if any sources were removed
      if (activeSources.length !== currentPersistentSourceOrder.length) {
        dispatch(setPersistentSourceOrder(activeSources));

        // Also clean up the sourceLastSeen object
        const cleanedSourceLastSeen = {};
        activeSources.forEach(sourceId => {
          if (currentSourceLastSeen[sourceId]) {
            cleanedSourceLastSeen[sourceId] = currentSourceLastSeen[sourceId];
          }
        });
        dispatch(setSourceLastSeen(cleanedSourceLastSeen));

        // Clean up colors for removed sources
        const cleanedColors = {};
        activeSources.forEach(sourceId => {
          if (currentPersistentSourceColors[sourceId]) {
            cleanedColors[sourceId] = currentPersistentSourceColors[sourceId];
          }
        });
        dispatch(setPersistentSourceColors(cleanedColors));

        if (process.env.NODE_ENV === 'development') {
          console.log('Cleaned up inactive sources. Active sources:', activeSources);
        }
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(cleanupInterval);
  }, [dispatch, sourceActivityTimeout]); // Get monitoring values from store directly to avoid stale interval closures.

  // Get source name by UUID with caching
  const getSourceName = useCallback((sourceId) => {
    if (!sourceId) return 'Unknown Source';

    // Check cache first
    if (sourceNameCache.has(sourceId)) {
      return sourceNameCache.get(sourceId);
    }

    // If no sources available, return formatted ID and cache it
    if (!sources || !Array.isArray(sources)) {
      const fallbackName = sourceId === 'unknown' ? 'Unknown Source' : `Source (${sourceId.substring(0, 8)}...)`;
      sourceNameCache.set(sourceId, fallbackName);
      return fallbackName;
    }

    // Look up source in the sources array
    const source = sources.find(s => s.source_id === sourceId);
    const resultName = source ? source.name : `Source (${sourceId.substring(0, 8)}...)`;

    // Cache the result
    sourceNameCache.set(sourceId, resultName);

    return resultName;
  }, [sources]);

  // Extract active sources from the processor-maintained monitoring snapshot.
  const messageSources = useMemo(() => {
    if (!Array.isArray(persistentSourceOrder)) {
      return [];
    }

    const currentTime = Date.now();

    return persistentSourceOrder
      .filter(sourceId => {
        const lastSeen = sourceLastSeen[sourceId];
        return lastSeen && (currentTime - lastSeen) < sourceActivityTimeout;
      })
      .map(sourceId => ({
        id: sourceId,
        name: getSourceName(sourceId)
      }));
  }, [persistentSourceOrder, sourceLastSeen, getSourceName, sourceActivityTimeout]);

  // Generate persistent colors for sources
  const sourceColors = useMemo(() => {
    // Start with existing persistent colors
    const colorMap = { ...persistentSourceColors };

    // Sort sources by ID to ensure consistent ordering
    const sortedSources = [...messageSources].sort((a, b) => a.id.localeCompare(b.id));

    // Assign colors for any new sources
    sortedSources.forEach(source => {
      if (!colorMap[source.id]) {
        colorMap[source.id] = getSourceColor(source.id, colorMap);
      }
    });

    return colorMap;
  }, [messageSources, persistentSourceColors]);

  useEffect(() => {
    const hasNewColors = Object.keys(sourceColors).some(sourceId => (
      sourceColors[sourceId] !== persistentSourceColors[sourceId]
    ));

    if (hasNewColors) {
      dispatch(setPersistentSourceColors(sourceColors));
    }
  }, [sourceColors, persistentSourceColors, dispatch]);

  // Get source names
  const sourceNames = useMemo(() => {
    const nameMap = {};

    messageSources.forEach(source => {
      nameMap[source.id] = source.name;
    });

    return nameMap;
  }, [messageSources]);

  const selectedSourceIds = useMemo(() => {
    const activeSourceIds = messageSources.map(source => source.id);

    if (Array.isArray(selectedSource)) {
      const selectedActiveSources = selectedSource.filter(sourceId => activeSourceIds.includes(sourceId));
      return selectedActiveSources.length > 0 ? selectedActiveSources : activeSourceIds;
    }

    if (selectedSource && selectedSource !== 'total' && activeSourceIds.includes(selectedSource)) {
      return [selectedSource];
    }

    return activeSourceIds;
  }, [messageSources, selectedSource]);

  const isAllSourcesSelected = useMemo(() => {
    if (messageSources.length === 0) {
      return true;
    }

    return selectedSourceIds.length === messageSources.length &&
      messageSources.every(source => selectedSourceIds.includes(source.id));
  }, [messageSources, selectedSourceIds]);

  const selectedSourceIdSet = useMemo(() => {
    return new Set(selectedSourceIds);
  }, [selectedSourceIds]);

  useEffect(() => {
    if (!messageRate || messageRate === 0) return;
    setMessagesPerSecond(messageRate);
    const timeout = setTimeout(() => setMessagesPerSecond(null), 15_000);
    return () => clearTimeout(timeout);
  }, [messageRate]);

  const filteredSelectedIntervalMessages = useMemo(() => {
    if (isAllSourcesSelected) {
      return selectedIntervalMessages;
    }

    return selectedIntervalMessages.filter(message => (
      selectedSourceIdSet.has(message.sourceId || 'unknown')
    ));
  }, [isAllSourcesSelected, selectedIntervalMessages, selectedSourceIdSet]);

  // Use the passed connect/disconnect functions from the hook

  // Handle chart point click
  const handlePointClick = useCallback((point) => {
    // Use the monitoring data processor to handle point clicks
    monitoringDataProcessor.handlePointClick(point, sources);
  }, [sources]);

  // Handle chart range selection
  const handleRangeSelect = useCallback((range) => {
    // Use the same monitoring data processor path as point clicks
    monitoringDataProcessor.handleRangeSelect(range, sources);
  }, [sources]);

  // Clear selected interval/range and return the details panel to live mode
  const handleClearSelection = useCallback(() => {
    dispatch(setSelectedInterval(null));
    dispatch(setSelectedIntervalMessages([]));
  }, [dispatch]);

  // Handle view record
  const handleViewRecord = useCallback((message) => {
    setSelectedRecord(message);
    setShowRecordModal(true);
  }, []);

  // Handle close record modal
  const handleCloseRecordModal = useCallback(() => {
    setShowRecordModal(false);
    setSelectedRecord(null);
  }, []);

  // Handle legend item click
  const handleLegendClick = useCallback((sourceId) => {
    if (sourceId === 'total') {
      dispatch(setSelectedSource('total'));
      return;
    }

    const activeSourceIds = messageSources.map(source => source.id);
    const currentSelection = isAllSourcesSelected ? [] : selectedSourceIds;

    let nextSelection;
    if (currentSelection.includes(sourceId)) {
      nextSelection = currentSelection.filter(id => id !== sourceId);
    } else {
      nextSelection = [...currentSelection, sourceId];
    }

    if (nextSelection.length === 0 || nextSelection.length === activeSourceIds.length) {
      dispatch(setSelectedSource('total'));
      return;
    }

    dispatch(setSelectedSource(nextSelection));
  }, [dispatch, isAllSourcesSelected, messageSources, selectedSourceIds]);

  // Clear all data
  const handleClearData = useCallback(() => {
    dispatch(clearMonitoringMessages());
    dispatch(clearMonitoringData());
    monitoringDataProcessor.clearData();
    // Note: clearMonitoringData now handles resetting all persistent state
  }, [dispatch]);

  // The WebSocket hook handles auto-connection and cleanup

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Data Monitor</h2>
        <div className="d-flex align-items-center">
          {connecting ? (
            <Badge bg="warning" className="me-2">
              <Spinner animation="border" size="sm" className="me-1" />
              Connecting...
            </Badge>
          ) : connected ? (
            <Badge bg="success" className="me-2">
              ● Live Feed Connected
            </Badge>
          ) : (
            <Badge bg="danger" className="me-2">
              ● Disconnected
            </Badge>
          )}
          {lastUpdateTime && (
            <small className="text-muted">
              Last update: {new Date(lastUpdateTime).toLocaleTimeString()}
            </small>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="warning" className="mb-3">
          <strong>Connection Issue:</strong> {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <div className="d-flex align-items-center gap-2 me-2">
                <Form.Label className="mb-0 text-muted small fw-bold">Rate Interval:</Form.Label>
                <Form.Select
                  size="sm"
                  value={rateIntervalMs}
                  onChange={e => setRateIntervalMs(parseInt(e.target.value))}
                  style={{ width: 'auto' }}
                >
                  {rateIntervalOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              {connected ? (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={disconnectWebSocket}
                  disabled={connecting}
                  className="me-2"
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={connectWebSocket}
                  disabled={connecting}
                  className="me-2"
                >
                  {connecting ? 'Connecting...' : 'Connect'}
                </Button>
              )}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleClearData}
              >
                Clear Data
              </Button>
            </div>
          </div>

          <Row className="mb-3">
            <Col md={4}>
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Connection Info</h5>
                    {connecting ? (
                      <Badge bg="warning" text="dark">Connecting...</Badge>
                    ) : connected ? (
                      <Badge bg="success">Connected</Badge>
                    ) : error ? (
                      <Badge bg="danger">Error</Badge>
                    ) : userDisconnected ? (
                      <Badge bg="secondary">Manually Disconnected</Badge>
                    ) : (
                      <Badge bg="secondary">Disconnected</Badge>
                    )}
                  </div>
                  <Table size="sm" borderless className="mb-0">
                    <tbody>
                      <tr>
                        <th className="fw-semibold ps-0 text-end" style={{ width: '6.75rem' }}>Messages:</th>
                        <td>
                          <div className="d-flex align-items-center gap-2 justify-content-between">
                            {totalMessagesReceived.toLocaleString()}
                            {messagesPerSecond !== null && (
                              <span title="Average over the last minute." className="text-muted ms-1">({Math.ceil(messagesPerSecond)} msg/s)</span>
                            )}
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <th className="fw-semibold ps-0 text-end">Sources:</th>
                        <td>{messageSources.length}</td>
                      </tr>
                      <tr>
                        <th className="fw-semibold ps-0 text-end">Last Update:</th>
                        <td>{lastUpdateTime ? formatUTCTime(lastUpdateTime) : 'N/A'}</td>
                      </tr>
                      <tr>
                        <th className="fw-semibold ps-0 text-end">Latency:</th>
                        <td>
                          <LatencyStatsDisplay latencyStats={latencyStats} />
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
            <Col md={8}>
              <Card className="h-100">
                <Card.Body>
                  <h5>Sources</h5>
                  <div className="d-flex flex-wrap gap-3">
                    {messageSources.length === 0 ? (
                      <p className="text-muted mb-0">No sources detected yet</p>
                    ) : (
                      <>
                        {/* All Sources option - only show if multiple sources */}
                        {messageSources.length > 1 && (
                          <div
                            className="d-flex align-items-center gap-2"
                            style={{
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: isAllSourcesSelected ? '#e3f2fd' : 'transparent',
                              border: isAllSourcesSelected ? '1px solid #2196f3' : '1px solid transparent'
                            }}
                            onClick={() => handleLegendClick('total')}
                          >
                            <div
                              style={{
                                width: '12px',
                                height: '12px',
                                backgroundColor: '#666',
                                borderRadius: '50%',
                                flexShrink: 0
                              }}
                            />
                            <span className="text-dark fw-medium">All Sources</span>
                          </div>
                        )}

                        {/* Individual sources */}
                        {messageSources.map(source => (
                          <div
                            key={source.id}
                            className="d-flex align-items-center gap-2"
                            style={{
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: !isAllSourcesSelected && selectedSourceIdSet.has(source.id) ? '#e3f2fd' : 'transparent',
                              border: !isAllSourcesSelected && selectedSourceIdSet.has(source.id) ? '1px solid #2196f3' : '1px solid transparent'
                            }}
                            onClick={() => handleLegendClick(source.id)}
                          >
                            <div
                              style={{
                                width: '12px',
                                height: '12px',
                                backgroundColor: sourceColors[source.id] || '#6c757d',
                                borderRadius: '50%',
                                flexShrink: 0
                              }}
                            />
                            <span className="text-dark fw-medium">{source.name}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <TimeSeriesChart
            data={timeSeriesData}
            sources={messageSources}
            sourceColors={sourceColors}
            selectedSourceIds={selectedSourceIds}
            onPointClick={handlePointClick}
            onRangeSelect={handleRangeSelect}
            onClearSelection={handleClearSelection}
            bucketDuration={rateIntervalMs}
            selectedDuration={selectedTimeWindowMs}
            onDurationChange={setSelectedTimeWindowMs}
            durationOptions={TIME_WINDOW_OPTIONS}
          />

          <MessageDetails
            messages={filteredSelectedIntervalMessages}
            sourceColors={sourceColors}
            sourceNames={sourceNames}
            timestamp={selectedInterval?.timestamp}
            endTimestamp={selectedInterval?.endTimestamp}
            isRange={selectedInterval?.isRange}
            onViewRecord={handleViewRecord}
          />
          </Card.Body>
        </Card>

      {/* Record Viewer Modal */}
      <Modal show={showRecordModal} onHide={handleCloseRecordModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Record Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <div>
              {/* Basic Information */}
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">Basic Information</h6>
                </Card.Header>
                <Card.Body>
                  <Table size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <th style={{ width: '150px' }}>Source</th>
                        <td>{selectedRecord.sourceName || 'Unknown'}</td>
                      </tr>
                      <tr>
                        <th>Timestamp</th>
                        <td>{selectedRecord.timestamp ? new Date(selectedRecord.timestamp).toISOString() : 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Leader ID</th>
                        <td>{selectedRecord.leader_id || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              {/* Raw Message Data */}
              <Card>
                <Card.Header>
                  <h6 className="mb-0">Raw Message Data</h6>
                </Card.Header>
                <Card.Body>
                  <pre className="bg-light p-3 rounded" style={{ fontSize: '0.875rem', maxHeight: '400px', overflow: 'auto' }}>
                    {JSON.stringify(selectedRecord, null, 2)}
                  </pre>
                </Card.Body>
              </Card>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseRecordModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MonitoringDashboard;
